import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { counselingManualApi } from '../api/counseling-manual.adapter';
import type { ManualListParams } from '../types/counseling-manual.types';

export const counselingManualQueryKeys = {
  all: ['counseling-manual'] as const,
  lists: () => [...counselingManualQueryKeys.all, 'list'] as const,
  list: (params: ManualListParams) => [...counselingManualQueryKeys.lists(), params] as const,
  detail: (cid: string) => [...counselingManualQueryKeys.all, 'detail', cid] as const,
  categoryTree: () => [...counselingManualQueryKeys.all, 'category-tree'] as const,
};

// PRD §3.1 — server-side list (C2). Filter/sort/pagination happen on the BE; the
// params are part of the query key so a change refetches the right page.
// `keepPreviousData` avoids a flash of empty table while the next page loads.
export function useManualsQuery(params: ManualListParams) {
  return useQuery({
    queryKey: counselingManualQueryKeys.list(params),
    queryFn: () => counselingManualApi.list(params),
    placeholderData: keepPreviousData,
  });
}

// PRD §3.5 — detail fetch when the edit panel opens. `cid` is null in create
// mode and the query stays disabled so we don't hit the adapter with a sentinel.
export function useManualDetailQuery(cid: string | null) {
  return useQuery({
    queryKey: counselingManualQueryKeys.detail(cid ?? ''),
    queryFn: () => counselingManualApi.get(cid as string),
    enabled: cid !== null,
  });
}

// PRD §3.2 — the Area/Sub-area/Item tree powers the cascade, the filters, and
// the Category Manager. Effectively static during a session, so cache it
// indefinitely; the Category Manager invalidates this key on mutation.
export function useCategoryTreeQuery() {
  return useQuery({
    queryKey: counselingManualQueryKeys.categoryTree(),
    queryFn: () => counselingManualApi.getCategoryTree(),
    staleTime: Infinity,
  });
}
